import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, Loader2, MessageSquareText, FileText, Scale, Sparkles } from "lucide-react";

const FEATURES_PREMIUM = [
  "Questions illimitées à l'assistant IA",
  "Analyses de documents illimitées",
  "Recherche juridique avancée illimitée",
  "Génération de lettres juridiques",
  "Historique complet de vos dossiers",
  "Support prioritaire",
];

const FEATURES_FREE = [
  "5 questions par jour",
  "2 analyses de documents par mois",
  "3 recherches juridiques par jour",
  "Historique de base",
];

export default function Premium() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [usage, setUsage] = useState(null);
  const [starting, setStarting] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      const r = await api.get("/me/usage");
      setUsage(r.data);
    } catch { /* ignore */ }
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    const MAX = 6;
    if (attempt >= MAX) {
      setVerifying(false);
      toast.error("Vérification du paiement expirée. Actualisez la page dans un instant.");
      return;
    }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setVerifying(false);
        toast.success("Paiement confirmé ! Vous êtes maintenant Premium 🎉");
        await checkAuth();
        await loadUsage();
        setParams({}, { replace: true });
        return;
      }
      if (r.data.status === "expired") {
        setVerifying(false);
        toast.error("La session de paiement a expiré.");
        setParams({}, { replace: true });
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setVerifying(false);
      toast.error("Impossible de vérifier le paiement.");
    }
  }, [checkAuth, loadUsage, setParams]);

  useEffect(() => {
    loadUsage();
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
    }
  }, [loadUsage, params, pollStatus]);

  const subscribe = async (plan) => {
    setStarting(plan);
    try {
      const r = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de démarrer le paiement");
      setStarting(null);
    }
  };

  const isPremium = usage?.is_premium;

  return (
    <div className="p-5 sm:p-10 max-w-5xl" data-testid="premium-page">
      <div className="flex items-center gap-2 text-gold mb-2">
        <Crown size={18} />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold">Abonnement</span>
      </div>
      <h1 className="font-serif text-4xl text-navy mb-3">Droits IA Premium</h1>
      <p className="text-slate-600 mb-8 max-w-2xl">Débloquez un accès illimité à votre assistant juridique et gagnez en sérénité face à vos démarches.</p>

      {verifying && (
        <div data-testid="payment-verifying" className="mb-8 flex items-center gap-3 bg-royal/10 border border-royal/20 text-royal rounded-lg px-5 py-4">
          <Loader2 className="animate-spin" size={20} />
          <span className="font-medium">Vérification de votre paiement en cours...</span>
        </div>
      )}

      {isPremium && (
        <div data-testid="premium-active-banner" className="mb-8 flex items-start gap-3 bg-gold/10 border border-gold/30 rounded-lg px-5 py-4">
          <Crown className="text-gold shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-serif text-lg text-navy">Vous êtes Premium ✨</p>
            <p className="text-sm text-slate-600">
              Offre {usage.subscription_plan === "annual" ? "annuelle" : "mensuelle"} active jusqu'au{" "}
              <span className="font-medium text-navy">{new Date(usage.subscription_expires_at).toLocaleDateString("fr-FR")}</span>.
            </p>
          </div>
        </div>
      )}

      {usage && !isPremium && (
        <div className="mb-10 bg-white border border-border rounded-lg p-6">
          <p className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Votre utilisation gratuite</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <UsageBar icon={MessageSquareText} label="Questions (jour)" u={usage.usage.question} />
            <UsageBar icon={FileText} label="Documents (mois)" u={usage.usage.document} />
            <UsageBar icon={Scale} label="Recherches (jour)" u={usage.usage.search} />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Free plan */}
        <div className="bg-white border border-border rounded-xl p-8">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">Gratuit</p>
          <p className="font-serif text-4xl text-navy mt-2">0€</p>
          <p className="text-sm text-slate-500 mb-6">Pour découvrir Droits IA</p>
          <ul className="space-y-3">
            {FEATURES_FREE.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                <Check size={16} className="text-slate-400 shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Premium plan */}
        <div className="relative bg-navy text-white rounded-xl p-8 overflow-hidden">
          <div className="absolute top-0 right-0 bg-gold text-navy text-xs font-bold px-4 py-1 rounded-bl-lg tracking-wide">POPULAIRE</div>
          <div className="flex items-center gap-2 text-gold">
            <Crown size={16} />
            <p className="text-xs uppercase tracking-wider font-semibold">Premium</p>
          </div>
          <div className="mt-3 flex items-end gap-2">
            <p className="font-serif text-4xl">19,99€</p>
            <span className="text-white/60 text-sm mb-1">/ mois</span>
          </div>
          <p className="text-sm text-white/60 mb-6">ou 199€/an (2 mois offerts)</p>
          <ul className="space-y-3 mb-8">
            {FEATURES_PREMIUM.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                <Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>

          <div className="space-y-3">
            <button
              data-testid="subscribe-monthly-btn"
              onClick={() => subscribe("monthly")}
              disabled={!!starting || verifying}
              className="w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-navy font-semibold py-3 rounded-md transition-all duration-200 disabled:opacity-50"
            >
              {starting === "monthly" ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              {isPremium ? "Prolonger — Mensuel · 19,99€" : "S'abonner — Mensuel · 19,99€"}
            </button>
            <button
              data-testid="subscribe-annual-btn"
              onClick={() => subscribe("annual")}
              disabled={!!starting || verifying}
              className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold py-3 rounded-md transition-all duration-200 disabled:opacity-50"
            >
              {starting === "annual" ? <Loader2 className="animate-spin" size={18} /> : <Crown size={18} className="text-gold" />}
              {isPremium ? "Prolonger — Annuel · 199€" : "S'abonner — Annuel · 199€"}
            </button>
          </div>
          <p className="text-[11px] text-white/40 mt-4 text-center">Paiement sécurisé via Stripe · Sans engagement</p>
        </div>
      </div>

      <button onClick={() => navigate("/dashboard")} className="mt-8 text-sm text-slate-500 hover:text-navy transition-colors">← Retour au tableau de bord</button>
    </div>
  );
}

const UsageBar = ({ icon: Icon, label, u }) => {
  const pct = u.limit ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
  const full = u.limit && u.used >= u.limit;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 text-sm text-slate-600"><Icon size={14} className="text-royal" /> {label}</span>
        <span className={`text-sm font-medium ${full ? "text-red-500" : "text-navy"}`}>{u.used}/{u.limit}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${full ? "bg-red-400" : "bg-royal"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
